import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useChateauImages } from "@/hooks/useChateauImages";

interface RecommendationGridProps {
  recommendations: any[];
  loading: boolean;
  hasValidCriteria: boolean;
  compact?: boolean;
}

export const RecommendationGrid = ({ 
  recommendations, 
  loading, 
  hasValidCriteria, 
  compact = false 
}: RecommendationGridProps) => {
  if (loading) {
    return (
      <div className="mt-8 p-6 bg-white/10 rounded-lg text-center">
        <p className="text-sm opacity-90">Chargement des recommandations...</p>
      </div>
    );
  }

  if (!hasValidCriteria) {
    return null;
  }

  if (recommendations.length === 0) {
    return (
      <div className="mt-6 p-4 bg-white/10 rounded-lg text-center">
        <p className="text-sm opacity-90">
          Aucun château ne correspond à vos critères. Contactez-nous pour des solutions personnalisées !
        </p>
      </div>
    );
  }

  return (
    <div className="mt-8 p-6 bg-white/10 rounded-lg">
      <h4 className="text-lg font-semibold mb-4">Nos recommandations pour vous :</h4>
      <div className={`grid grid-cols-1 ${compact ? 'md:grid-cols-3 gap-3' : 'md:grid-cols-3 lg:grid-cols-3 gap-4'}`}>
        {recommendations.map((chateau) => (
          <ChateauRecommendationCard 
            key={chateau.id} 
            chateau={chateau} 
            compact={compact}
          />
        ))}
      </div>
    </div>
  );
};

interface ChateauRecommendationCardProps {
  chateau: any;
  compact: boolean;
}

const ChateauRecommendationCard = ({ chateau, compact }: ChateauRecommendationCardProps) => {
  const { imageUrls, isLoading } = useChateauImages(chateau.id);
  
  // Use first image from database or fallback
  const imageUrl = imageUrls.length > 0 ? imageUrls[0] : '/placeholder.svg';
  
  return (
    <div className="bg-white/20 rounded-lg p-4 text-center backdrop-blur-sm">
      <img 
        src={imageUrl}
        alt={chateau.nom}
        className={`${compact ? 'w-32 h-32' : 'w-40 h-40'} object-cover rounded-lg mb-3 mx-auto`}
        loading="lazy"
        onError={(e) => {
          console.log('[RecommendationGrid] Image error for:', chateau.nom, imageUrl);
          e.currentTarget.src = '/placeholder.svg';
        }}
        onLoad={() => {
          console.log('[RecommendationGrid] Image loaded:', chateau.nom, imageUrl);
        }}
      />
      <h5 className="font-semibold mb-1 text-sm">{chateau.nom}</h5>
      <p className="text-sm opacity-90 mb-1 font-bold">{chateau.prix}</p>
      <p className="text-xs opacity-75 mb-2">{chateau.taille}</p>
      <Link to={`/chateau/${chateau.id}`}>
        <Button variant="default" size="sm" className="w-full text-xs">
          Voir
        </Button>
      </Link>
    </div>
  );
};